import React from "react";
import "./WhereIsEverythingGoing.module.css";
import "@/components/cosmic/cosmic.css";

// Foundation components from Origin & Beyond
import { OriginPageShell, PageHero, EvidenceKey, RelatedExperiences, JourneyBar, SourceList, DisclaimerNote } from "@/components/originBeyond";

// Local section components
import { HeroVisual } from "./sections/HeroVisual";
import { TimeHorizonsSection } from "./sections/TimeHorizonsSection";
import { SunLifecycleSection } from "./sections/SunLifecycleSection";
import { FateOfUniverseSection } from "./sections/FateOfUniverseSection";
import { TheLongDarkSection } from "./sections/TheLongDarkSection";
import { HumanFuturesSection } from "./sections/HumanFuturesSection";
import { WhyItMattersSection } from "./sections/WhyItMattersSection";

// Scholarly sources dataset
import { FUTURE_SOURCES } from "./futureData";

/**
 * WhereIsEverythingGoing - Deep-inquiry Question 04
 * Cosmic Fates, Deep Time & The Destiny of Matter
 */
export default function WhereIsEverythingGoingPage() {
  return <OriginPageShell accent="cyan">
      {/* 1. Page Hero with Signature Visual (questionNumber 4) */}
      <PageHero questionNumber={4} eyebrow="Cosmic Fates & Deep Time" title="Where Is Everything Going?" subtitle="From the brightening sun and colliding galaxies to accelerating dark energy, black hole evaporation, and the long thermodynamic dark: how physics charts the destiny of reality.">
        <HeroVisual />
      </PageHero>

      {/* Intro transition note: Near-certain physics to pure imagination */}
      <section className="w-full max-w-full min-w-0 px-4 sm:px-6 lg:px-8 -mt-4 mb-8">
        <div className="max-w-[76rem] mx-auto min-w-0">
          <DisclaimerNote tone="science" title="From Near-Certain Physics to Theoretical Frontier">
            <p className="leading-relaxed">
              As we extrapolate forward in time, our scientific certainty shifts. Nuclear
              fusion and stellar evolution over the next several billion years are bounded by
              well-established physics. Cosmological expansion over tens of billions of years
              depends on active dark energy research. Beyond a trillion years, into the
              evaporation of black holes and the fate of fundamental matter, we navigate the
              outer frontiers of theoretical physics and mathematical speculation. Every model
              on this page is tagged with its epistemic status.
            </p>
          </DisclaimerNote>
        </div>
      </section>

      {/* Epistemology Guide */}
      <section className="w-full max-w-full min-w-0 px-4 sm:px-6 lg:px-8 mb-12">
        <div className="max-w-[76rem] mx-auto min-w-0">
          <EvidenceKey />
        </div>
      </section>

      {/* 2. Zoom out in time: 5 Horizons TabExplorer */}
      <TimeHorizonsSection />

      {/* 3. The Sun's Long Goodbye: Interactive Solar Life & Earth Habitability */}
      <SunLifecycleSection />

      {/* 4. Fate of the Universe: Big Freeze, Big Rip, Big Crunch, Vacuum Decay, Big Bounce + Dark Energy Dial */}
      <FateOfUniverseSection />

      {/* 5. The Long Dark: Adams & Laughlin 1997 Era Strip */}
      <TheLongDarkSection />

      {/* 6. Possible Futures for Humanity: ExpandList */}
      <HumanFuturesSection />

      {/* 7. Reflective Closing: Why It Matters Now */}
      <WhyItMattersSection />

      {/* 8. Related Experiences Navigation */}
      <section className="w-full max-w-full min-w-0 py-12 sm:py-16 px-4 sm:px-6 lg:px-8 border-t border-border/30">
        <div className="max-w-[76rem] mx-auto min-w-0">
          <RelatedExperiences keys={["kardashev", "atlas", "creator", "whatIf", "discoveries"]} title="Explore Related Cosmic Portals" />
        </div>
      </section>

      {/* 9. Expedition Journey Bar (Current: "future") */}
      <section className="w-full max-w-full min-w-0 py-8 px-4 sm:px-6 lg:px-8 border-t border-border/30">
        <div className="max-w-[76rem] mx-auto min-w-0">
          <JourneyBar current="future" />
        </div>
      </section>

      {/* 10. Scholarly Citation Footnotes (Sources Last) */}
      <section className="w-full max-w-full min-w-0 py-12 sm:py-16 px-4 sm:px-6 lg:px-8 border-t border-border/30">
        <div className="max-w-[76rem] mx-auto min-w-0">
          <SourceList sources={FUTURE_SOURCES} title="Scholarly & Cosmological Archival Sources" />
        </div>
      </section>
    </OriginPageShell>;
}